import {useEffect, useMemo, useRef} from 'react';
import {Select} from '@gravity-ui/uikit';
import {useCategoryGroups} from '../../hooks';
import {CategoryGroupModel, CategoryGroupsWithMetaModel} from '../../model';
import styles from './SelectCategoryGroups.module.scss';

interface SelectCategoryGroupsProps {
  value: string[];
  onUpdate: (value: string[]) => void;
  name?: string;
  size?: 's' | 'm' | 'l';
  placeholder?: string;
  multiple?: boolean;
  error?: string | string[] | false;
}

export const SelectCategoryGroups = ({
  value,
  onUpdate,
  name,
  size = 'm',
  placeholder = 'Группа категорий',
  multiple = false,
  error,
}: SelectCategoryGroupsProps) => {
  const {getInfiniteCategoryGroups} = useCategoryGroups({pageSize: 200});
  const groupSelectRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLButtonElement>(null);

  const {
    data: infiniteGroupsData,
    isLoading: isGroupsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = getInfiniteCategoryGroups;

  const groupOptions = useMemo(() => {
    return (
      infiniteGroupsData?.pages.flatMap((page: CategoryGroupsWithMetaModel) =>
        page.items.map((group: CategoryGroupModel) => ({
          value: group.id,
          content: group.name,
        })),
      ) || []
    );
  }, [infiniteGroupsData]);

  useEffect(() => {
    if (hasNextPage && !isGroupsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isGroupsLoading, isFetchingNextPage, fetchNextPage]);

  const handleScroll = () => {
    if (!groupSelectRef.current) return;
    const {scrollTop, scrollHeight, clientHeight} = groupSelectRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20 && hasNextPage && !isGroupsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        const popupElement = document.querySelector('.g-select-popup__content');
        if (popupElement) {
          groupSelectRef.current = popupElement as HTMLDivElement;
          popupElement.addEventListener('scroll', handleScroll);
        }
      }, 100);
    } else if (groupSelectRef.current) {
      groupSelectRef.current.removeEventListener('scroll', handleScroll);
      groupSelectRef.current = null;
    }
  };

  return (
    <Select
      className={styles.select}
      size={size}
      ref={selectRef}
      name={name}
      options={groupOptions}
      onUpdate={onUpdate}
      value={value}
      onOpenChange={handleOpenChange}
      loading={isGroupsLoading}
      placeholder={placeholder}
      multiple={multiple}
      errorPlacement="inside"
      errorMessage={error}
      validationState={error ? 'invalid' : undefined}
      filterable
      filterPlaceholder="Поиск"
    />
  );
};
